import regionalSettingsStore, {
  DEFAULT_DATE_FORMAT,
  DEFAULT_TIME_ZONE
} from 'stores/regionalSettingsStore'

const CIVIL_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})/
const UTC_SUFFIX_PATTERN = /(Z|[+-]\d{2}:\d{2})$/i

const pad = (value: number) => String(value).padStart(2, '0')

const formatParts = (year: number, month: number, day: number, format: string) => {
  const yyyy = String(year).padStart(4, '0')
  const MM = pad(month)
  const dd = pad(day)
  if (format === 'MM/dd/yyyy') return `${MM}/${dd}/${yyyy}`
  if (format === 'yyyy/MM/dd') return `${yyyy}/${MM}/${dd}`
  return `${dd}/${MM}/${yyyy}`
}

const getPreferences = () => ({
  dateFormat: regionalSettingsStore.dateFormat || DEFAULT_DATE_FORMAT,
  timeZoneId: regionalSettingsStore.timeZoneId || DEFAULT_TIME_ZONE
})

const asUtcDate = (value: string | Date) => {
  if (value instanceof Date) return new Date(value.getTime())
  const normalized = UTC_SUFFIX_PATTERN.test(value) ? value : `${value}Z`
  return new Date(normalized)
}

const getZonedParts = (value: string | Date, timeZoneId: string) => {
  const date = asUtcDate(value)
  if (Number.isNaN(date.getTime())) return null

  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timeZoneId,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23'
  }).formatToParts(date)
  const values = Object.fromEntries(parts.map(part => [part.type, part.value]))
  return {
    year: Number(values.year),
    month: Number(values.month),
    day: Number(values.day),
    hour: values.hour,
    minute: values.minute,
    second: values.second
  }
}

export const formatCivilDate = (
  value?: string | Date | null,
  dateFormat = getPreferences().dateFormat
) => {
  if (!value) return ''
  if (value instanceof Date) {
    return formatParts(value.getFullYear(), value.getMonth() + 1, value.getDate(), dateFormat)
  }

  const match = CIVIL_DATE_PATTERN.exec(value)
  if (!match) return ''
  return formatParts(Number(match[1]), Number(match[2]), Number(match[3]), dateFormat)
}

export const formatUtcDateTime = (
  value?: string | Date | null,
  options: { dateFormat?: string; timeZoneId?: string; includeSeconds?: boolean } = {}
) => {
  if (!value) return ''
  const preferences = getPreferences()
  const parts = getZonedParts(value, options.timeZoneId || preferences.timeZoneId)
  if (!parts) return ''

  const date = formatParts(
    parts.year,
    parts.month,
    parts.day,
    options.dateFormat || preferences.dateFormat
  )
  const time = options.includeSeconds
    ? `${parts.hour}:${parts.minute}:${parts.second}`
    : `${parts.hour}:${parts.minute}`
  return `${date} ${time}`
}

export const toApiCivilDate = (value?: string | Date | null) => {
  if (!value) return ''
  if (typeof value === 'string') {
    const match = CIVIL_DATE_PATTERN.exec(value)
    if (match) return `${match[1]}-${match[2]}-${match[3]}`
  }

  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

export const parseApiCivilDate = (value?: string | Date | null) => {
  if (!value) return null
  if (value instanceof Date) return new Date(value.getTime())
  const match = CIVIL_DATE_PATTERN.exec(value)
  if (!match) return null
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
}

export const toUtcIso = (value: string | Date) => asUtcDate(value).toISOString()

export const formatDateObject = (value: Date) => formatCivilDate(value)

export const parseDisplayDate = (value: string) => {
  const { dateFormat } = getPreferences()
  const parts = value.split('/').map(Number)
  if (parts.length !== 3 || parts.some(Number.isNaN)) return new Date(NaN)

  const [first, second, third] = parts
  const year = dateFormat === 'yyyy/MM/dd' ? first : third
  const month = dateFormat === 'MM/dd/yyyy' ? first : second
  const day = dateFormat === 'yyyy/MM/dd' ? third : dateFormat === 'MM/dd/yyyy' ? second : first
  const date = new Date(year, month - 1, day)
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day
    ? date
    : new Date(NaN)
}

export const getDatePlaceholder = () => getPreferences().dateFormat
  .replace('yyyy', 'aaaa')
  .replace('MM', 'mm')
  .replace('dd', 'dd')

export const todayCivil = (timeZoneId = getPreferences().timeZoneId) => {
  const parts = getZonedParts(new Date(), timeZoneId)
  return parts ? `${parts.year}-${pad(parts.month)}-${pad(parts.day)}` : ''
}

export const currentMonthCivilRange = (timeZoneId = getPreferences().timeZoneId) => {
  const today = todayCivil(timeZoneId)
  const match = CIVIL_DATE_PATTERN.exec(today)
  if (!match) return { from: '', to: '' }
  const year = Number(match[1])
  const month = Number(match[2])
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate()
  return {
    from: `${match[1]}-${match[2]}-01`,
    to: `${match[1]}-${match[2]}-${pad(lastDay)}`
  }
}

export const getRegionalPreferences = getPreferences
